import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  InputAdornment,
  Tooltip,
  Typography,
  Box,
  Button,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
} from '@mui/material';
import { Refresh, Delete, Info } from '@mui/icons-material';
import {
  createRow,
  useMaterialReactTable,
  MaterialReactTable,
  MRT_EditActionButtons as EditActionButtons,
} from 'material-react-table';
import { isIP } from 'is-ip';
import {
  getDeploymentModbusAction,
  upsertModbusDeploymentAction,
  deleteModbusDeploymentAction,
} from '../../redux/actions/deployments';
import { defaultTableOptions } from '../util/data-table';
import { unique } from '../../utils/collections';

function isFailed(value) {
  return value === 'FAILED';
}
function determineColor(value) {
  if (isFailed(value)) return 'error';
  if (value === 'IN_PROGRESS') return 'grey';
  return 'black';
}
function buildTemplateIdentifier(template) {
  return [template.vendor, template.name].join(' - ') ?? template.template_id;
}

const TemplateDetails = ({ template }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'register',
        header: 'Register',
      },
      {
        id: 'data_type',
        header: 'Data Type',
        accessorFn: (row) => row.data_type.name,
      },
      {
        accessorKey: 'point_type',
        header: 'Point Type',
      },
      {
        accessorKey: 'units',
        header: 'Units',
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
    ],
    []
  );

  const templateTable = useMaterialReactTable({
    ...defaultTableOptions,
    initialState: {},
    columns,
    data: template?.fields ?? [],
  });

  return template ? (
    <Box>
      <DialogTitle variant="h4">{buildTemplateIdentifier(template)} </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <DialogContentText>
          <strong>Equipment Type:</strong> {template.equipment_type}
        </DialogContentText>
        <DialogContentText>
          <strong> Fields:</strong>
        </DialogContentText>
        <MaterialReactTable table={templateTable} />
      </DialogContent>
    </Box>
  ) : null;
};

const DeploymentConfiguration = ({
  deployment,
  modbusDeployments,
  canEdit,
  buildingsById,
  templates,
  reloadModbus,
  upsertModbus,
  deleteModbus,
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [templateInfo, setTemplateInfo] = useState(undefined);

  const templateOptions = useMemo(
    () =>
      Object.values(templates).map((template) => ({
        value: template.template_id,
        label: buildTemplateIdentifier(template),
      })),
    [templates]
  );

  const columns = useMemo(() => {
    function tooltipForStatus(modbusStatus) {
      if (modbusStatus === 'NEW') return 'Modbus setup has been scheduled';
      if (modbusStatus === 'IN_PROGRESS')
        return 'Modbus setup is currently being run on the collector';
      if (modbusStatus === 'FAILED') return 'Modbus setup failed on the collector';
      if (modbusStatus === 'SUCCESS') return 'Modbus setup completed on the collector';
      return 'Unknown modbus status';
    }

    return [
      {
        accessorKey: 'ip',
        header: 'Controller',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.ip,
          helperText: validationErrors?.ip,
          onBlur: ({ target }) =>
            setValidationErrors({
              ...validationErrors,
              ip: isIP(target.value) ? null : 'Must be a valid IPv4 Address',
            }),
        },
      },
      {
        accessorKey: 'port',
        header: 'Port',
        muiEditTextFieldProps: {
          required: true,
          // Using tel to avoid showing arrows (which do not work)
          type: 'tel',
          error: !!validationErrors?.port,
          helperText: validationErrors?.port,
          onBlur: ({ target }) =>
            setValidationErrors({
              ...validationErrors,
              port:
                target.value >= 1 && target.value <= 65535
                  ? null
                  : 'Port must be between [1-65535]',
            }),
        },
      },
      {
        accessorKey: 'unit',
        header: 'Unit Identifier',
        muiEditTextFieldProps: {
          required: true,
          // Using tel to avoid showing arrows (which do not work)
          type: 'tel',
          error: !!validationErrors?.unit,
          helperText: validationErrors?.unit,
          onBlur: ({ target }) =>
            setValidationErrors({
              ...validationErrors,
              unit:
                target.value >= 1 && target.value <= 247
                  ? null
                  : 'Unit Identifier must be between [1-247]',
            }),
        },
      },
      {
        // id is important, otherwise payload is borked
        id: 'building_id',
        accessorKey: 'buildingId',
        header: 'Building',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ cell }) => <span>{buildingsById[cell.getValue()]?.name ?? cell.getValue()}</span>,
        editVariant: 'select',
        editSelectOptions: deployment?.buildings,
        muiEditTextFieldProps: {
          select: true,
          disabled: deployment?.buildings?.length <= 1,
          error: !!validationErrors?.buildingId,
          helperText: validationErrors?.buildingId,
          slotProps: {
            select: {
              renderValue: (bid) => buildingsById[bid]?.name ?? bid,
            },
          },
        },
      },
      {
        accessorKey: 'template_id',
        header: 'Vendor Device',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ cell }) => (
          <>
            {buildTemplateIdentifier(templates[cell.getValue()])}
            <IconButton
              sx={{ marginRight: '20px' }}
              onClick={() => setTemplateInfo(cell.getValue())}
            >
              <Info />
            </IconButton>
          </>
        ),

        editVariant: 'select',
        editSelectOptions: templateOptions,
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          error: !!validationErrors?.template_id,
          helperText: validationErrors?.template_id,
          slotProps: {
            select: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    sx={{ marginRight: '20px' }}
                    onClick={() => setTemplateInfo(cell.getValue())}
                  >
                    <Info />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }),
      },
      {
        accessorKey: 'poll_freq',
        header: 'Polling Frequency',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ cell }) => <span>{cell.getValue()}s</span>,
        muiEditTextFieldProps: {
          required: true,
          // Using tel to avoid showing arrows (which do not work)
          type: 'tel',
          error: !!validationErrors?.poll_freq,
          helperText: validationErrors?.poll_freq,
          slotProps: {
            input: {
              endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
            },
          },
          onBlur: ({ target }) =>
            setValidationErrors({
              ...validationErrors,
              poll_freq: target.value >= 60 ? null : 'Polling Frequency must be at least 60s',
            }),
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'checkbox',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={tooltipForStatus(renderedCellValue)}>
            <Typography variant="button" color={determineColor(renderedCellValue)}>
              {renderedCellValue}
            </Typography>
          </Tooltip>
        ),
        enableEditing: false,
        // hide from create modal
        Edit: () => null,
      },
    ];
  }, [buildingsById, templates, templateOptions, validationErrors, deployment]);

  const data = useMemo(() => modbusDeployments ?? [], [modbusDeployments]);
  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    initialState: {
      columnVisibility: {
        buildingId: unique(data.map(({ buildingId }) => buildingId))?.length > 1,
      },
    },
    columns,
    data,
    enableTopToolbar: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,

    positionActionsColumn: 'last',
    enableRowActions: canEdit,
    renderRowActions: ({ row }) =>
      isFailed(row.original.status) ? (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
          <IconButton
            onClick={() => {
              const { status, deployment_id, ...modbusConfig } = row.original;
              return upsertModbus(modbusConfig);
            }}
          >
            <Refresh />
          </IconButton>
          <IconButton
            onClick={() => {
              const { status, deployment_id, ...modbusConfig } = row.original;
              return deleteModbus(modbusConfig);
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      ) : null,

    renderToolbarInternalActions: () => (
      <IconButton variant="contained" onClick={reloadModbus}>
        <Refresh />
      </IconButton>
    ),
    renderBottomToolbarCustomActions: ({ table }) =>
      deployment.buildings?.length > 0 && canEdit ? (
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(
              createRow(table, {
                port: 502,
                unit: 1,
                template_id: templateOptions[0].value,
                buildingId: deployment.buildings[0].id,
                poll_freq: 60,
              })
            );
            setValidationErrors({ ip: 'IP address must be provided' });
          }}
        >
          Add New Modbus
        </Button>
      ) : null,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: ({ values, table }) =>
      Object.values(validationErrors).filter(Boolean).length === 0
        ? upsertModbus(values).then(table.setCreatingRow(null))
        : null,
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle variant="h4">Add New Modbus Device</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {internalEditComponents} {/* or render custom edit components here */}
        </DialogContent>
        <DialogActions>
          <EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    ),
  });
  return (
    <Box>
      <Dialog
        open={templateInfo}
        onClose={() => setTemplateInfo(undefined)}
        scroll="paper"
        maxWidth="lg"
      >
        <TemplateDetails template={templates[templateInfo]} />
      </Dialog>
      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
        Modbus Deployments
      </Typography>
      <MaterialReactTable table={mrt} />
    </Box>
  );
};

function mapStateToProps(state, { deployment }) {
  const {
    entities: {
      deployments: { modbus },
      buildings: { byId: buildingsById },
      modbus: { templates },
    },
  } = state;
  return {
    modbusDeployments: modbus[deployment.site] ?? [],
    canEdit: deployment.buildings.some((b) => b.canWrite) && deployment.active,
    buildingsById,
    templates,
  };
}

function mapDispatchToProps(dispatch, { deployment }) {
  return {
    reloadModbus: () => dispatch(getDeploymentModbusAction(deployment.site)),
    upsertModbus: (modbusConfig) =>
      dispatch(upsertModbusDeploymentAction(deployment.site, modbusConfig)),
    deleteModbus: (modbusConfig) =>
      dispatch(deleteModbusDeploymentAction(deployment.site, modbusConfig)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentConfiguration);
